import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Save, Search, History, ChevronLeft, ChevronRight, Bus, Clock, Users, StickyNote, FileDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useVehicleMap, brandOf, vehicleLabel, normPlate } from "@/lib/vehicles";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const shiftYmd = (ymd, delta) => {
  const d = new Date(ymd + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const DAY_TR = ["PAZAR", "PAZARTESİ", "SALI", "ÇARŞAMBA", "PERŞEMBE", "CUMA", "CUMARTESİ"];
const dayLabel = (ymd) => { try { return DAY_TR[new Date(ymd + "T00:00:00").getDay()]; } catch { return ""; } };

// Fixed line/vehicle structure. Kept in sync with backend SHIFT_LINE_STRUCTURE.
const LINE_STRUCTURE = [
  { line: "15A", label: "15A Hattı", vehicle_count: 6 },
  { line: "15B", label: "15B Hattı", vehicle_count: 4 },
  { line: "16A", label: "16A Hattı", vehicle_count: 1 },
  { line: "16İ", label: "16İ Hattı", vehicle_count: 4 },
  { line: "5A",  label: "5A Hattı",  vehicle_count: 2 },
  { line: "B1",  label: "B1 Hattı",  vehicle_count: 5 },
];

const buildEmptyRows = (structure) => {
  const out = [];
  structure.forEach((grp) => {
    for (let i = 1; i <= grp.vehicle_count; i++) {
      out.push({
        id: null,
        line: grp.line,
        vehicle_no: i,
        plate: "",
        shift1_personnel: "",
        change_time: "",
        shift2_personnel: "",
      });
    }
  });
  return out;
};

const mergeRows = (structure, existing) => {
  const base = buildEmptyRows(structure);
  const byKey = new Map((existing || []).map((r) => [`${r.line}-${r.vehicle_no}`, r]));
  return base.map((tpl) => {
    const found = byKey.get(`${tpl.line}-${tpl.vehicle_no}`);
    return found ? { ...tpl, ...found } : tpl;
  });
};

export default function ShiftSchedule() {
  const { user } = useAuth();
  const canEdit = ["admin", "editor", "data_entry"].includes(user?.role);

  const [date, setDate] = useState(todayYmd());
  const [structure, setStructure] = useState(LINE_STRUCTURE);
  const [rows, setRows] = useState(buildEmptyRows(LINE_STRUCTURE));
  const [dayNotes, setDayNotes] = useState("");
  const [personnelList, setPersonnelList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingMonthlyPdf, setDownloadingMonthlyPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Read-only PDF mode when a past date is selected. Today and future dates keep
  // the editable matrix so operators can plan the day.
  const isPastDate = useMemo(() => date < todayYmd(), [date]);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/shifts", { params: { date: d } });
      const struct = data.structure || LINE_STRUCTURE;
      setStructure(struct);
      setRows(mergeRows(struct, data.rows || []));
      setDayNotes(data.day_notes || "");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // Only load matrix data in edit mode (today or future). Past dates render as PDF.
    if (!isPastDate) load(date);
    else { setDirty(false); }
  }, [date, load, isPastDate]);

  // In past-date mode, fetch the PDF as a blob and render it inline via an iframe.
  useEffect(() => {
    let revoked = null;
    if (isPastDate) {
      setPdfLoading(true);
      setPdfUrl(null);
      api.get("/shifts/pdf", { params: { date }, responseType: "blob" })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          revoked = url;
          setPdfUrl(url);
        })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rapor yüklenemedi"))
        .finally(() => setPdfLoading(false));
    } else {
      setPdfUrl(null);
    }
    return () => { if (revoked) window.URL.revokeObjectURL(revoked); };
  }, [date, isPastDate]);
  // Update 29.20 — Şoför listesi: SADECE role_type=Şoför + bugüne kadar aktif
  // (employment_periods içinde o güne denk gelen açık dönem olmalı).
  useEffect(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const isActiveToday = (p) => {
      const periods = Array.isArray(p.employment_periods) ? p.employment_periods : [];
      if (!periods.length) return !!p.active;
      return periods.some((pr) => {
        const s = pr.start_date || null;
        const e = pr.end_date || null;
        if (s && s > todayIso) return false;
        if (e && e <= todayIso) return false;
        return true;
      });
    };
    api.get("/personnel").then((r) => {
      const all = r.data || [];
      const drivers = all
        .filter((p) => (p.role_type || "Şoför") === "Şoför" && isActiveToday(p))
        .map((p) => p.name);
      setPersonnelList(drivers);
    }).catch(() => {});
    api.get("/vehicles").then((r) => {
      const list = (r.data || [])
        .filter((v) => v.active !== false)
        .map((v) => ({ code: v.code || "", plate: v.plate || "", label: `${v.code || "?"} — ${v.plate || ""}`.trim(), display: `${v.code || "?"} — ${v.plate || ""}${v.brand ? ` — ${v.brand}` : ""}`.trim() }))
        .sort((a, b) => {
          const na = parseInt(a.code, 10); const nb = parseInt(b.code, 10);
          if (!isNaN(na) && !isNaN(nb)) return na - nb;
          return String(a.code).localeCompare(String(b.code));
        });
      setVehicles(list);
    }).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get("/shifts/pdf", {
        params: { date },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vardiya_Raporu_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF indirilemedi");
    } finally { setDownloadingPdf(false); }
  };

  const downloadMonthlyPdf = async () => {
    setDownloadingMonthlyPdf(true);
    try {
      const month = date.slice(0, 7); // YYYY-MM from active date
      const res = await api.get("/shifts/pdf/monthly", {
        params: { month },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vardiya_Aylik_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${month} aylık özet PDF hazır`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aylık PDF indirilemedi");
    } finally { setDownloadingMonthlyPdf(false); }
  };

  const updateCell = (rowIdx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [field]: value };
      return next;
    });
    setDirty(true);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // Only send rows that have at least one filled field (except line/vehicle_no which are always present)
      const payloadRows = rows.map((r) => ({
        id: r.id,
        line: r.line,
        vehicle_no: r.vehicle_no,
        plate: r.plate || "",
        shift1_personnel: r.shift1_personnel || "",
        change_time: r.change_time || "",
        shift2_personnel: r.shift2_personnel || "",
      })).filter((r) => r.plate || r.shift1_personnel || r.shift2_personnel || r.change_time);

      const { data } = await api.post("/shifts/bulk", {
        date,
        rows: payloadRows,
        day_notes: dayNotes,
      });
      toast.success(`${data.saved} satır kaydedildi`);
      // Reload to get canonical state (ids etc.)
      await load(date);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kayıt başarısız");
    } finally { setSaving(false); }
  };

  // Summary stats
  const filledRows = useMemo(() => rows.filter((r) => r.shift1_personnel || r.shift2_personnel || r.plate), [rows]);
  const totalVehicles = rows.length;
  const morningStaffCount = useMemo(() => rows.filter((r) => r.shift1_personnel).length, [rows]);
  const eveningStaffCount = useMemo(() => rows.filter((r) => r.shift2_personnel).length, [rows]);

  // Group rows by line for rendering
  const groupedRows = useMemo(() => {
    const map = {};
    rows.forEach((r, idx) => {
      if (!map[r.line]) map[r.line] = [];
      map[r.line].push({ ...r, _idx: idx });
    });
    return map;
  }, [rows]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto w-full" data-testid="shift-schedule-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">
            {isPastDate ? "Geçmiş Rapor · Salt Okunur" : "Günlük Operasyon"}
          </div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Vardiya Çizelgesi</h1>
          <p className="mt-1 text-sm text-slate-500">
            {isPastDate
              ? "Geçmiş tarihlerin çizelgesi resmi rapor formatında görüntülenir. Düzenleme yalnızca bugün ve ileri tarihlerde yapılabilir."
              : "Her hat için sabit araç sayısı. Personelleri seçin, günlük notları alt kısma ekleyin, tek tıkla kaydedin."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={downloadPdf}
            disabled={downloadingPdf}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="download-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" /> {downloadingPdf ? "Hazırlanıyor..." : "Günlük PDF"}
          </Button>
          <Button
            onClick={downloadMonthlyPdf}
            disabled={downloadingMonthlyPdf}
            className="bg-[#1E3A8A] hover:bg-[#1e40af] text-white"
            data-testid="download-monthly-pdf-btn"
            title="Seçili ayın tüm günlerini ve notları tek PDF'te dışa aktarır"
          >
            <FileDown className="w-4 h-4 mr-2" /> {downloadingMonthlyPdf ? "Hazırlanıyor..." : "Aylık Özet PDF"}
          </Button>
          <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="history-btn">
            <History className="w-4 h-4 mr-2" /> Geçmişi Görüntüle
          </Button>
        </div>
      </div>

      {/* Date navigator + summary */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <Button variant="outline" size="sm" onClick={() => setDate(shiftYmd(date, -1))} data-testid="prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Çizelge Tarihi</Label>
            <div className="flex items-center gap-2 mt-1">
              <CalendarIcon className="w-4 h-4 text-slate-500" />
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value || todayYmd())} className="h-10 max-w-[180px]" data-testid="date-picker" />
              <div className="text-lg font-heading font-bold text-[#1E3A8A] tracking-widest">{dayLabel(date)}</div>
              {isPastDate && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-slate-800 text-white" data-testid="readonly-badge">
                  Geçmiş · Rapor
                </span>
              )}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDate(shiftYmd(date, 1))} data-testid="next-day"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setDate(todayYmd())} data-testid="today-btn">Bugün</Button>
          {!isPastDate && (
            <div className="ml-auto flex gap-3 items-center flex-wrap">
              <StatChip icon={Bus} label="Toplam Araç" value={totalVehicles} color="text-slate-900" />
              <StatChip icon={Users} label="1. Vardiya" value={`${morningStaffCount}/${totalVehicles}`} color="text-amber-700" />
              <StatChip icon={Clock} label="2. Vardiya" value={`${eveningStaffCount}/${totalVehicles}`} color="text-blue-700" />
              {canEdit && (
                <Button onClick={saveAll} disabled={saving || !dirty} className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="save-all-btn">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Kaydediliyor..." : dirty ? "Vardiyayı Kaydet" : "Kayıtlı"}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {isPastDate ? (
        <PdfReportViewer url={pdfUrl} loading={pdfLoading} date={date} />
      ) : loading ? (
        <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-lg">Yükleniyor...</div>
      ) : (
        <div className="space-y-6">
          {structure.map((grp) => (
            <Card key={grp.line} className="overflow-hidden">
              <div className="bg-gradient-to-r from-[#1E3A8A] to-[#1e40af] px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center">
                    <Bus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-heading font-bold text-lg leading-none">{grp.label}</div>
                    <div className="text-blue-200 text-xs mt-0.5 font-mono">{grp.vehicle_count} Araç</div>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ tableLayout: "fixed" }} data-testid={`shift-table-${grp.line}`}>
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "6%" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2.5 text-center text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Sıra</th>
                      <th className="px-3 py-2.5 text-left text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Araç / Plaka</th>
                      <th className="px-3 py-2.5 text-left text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">1. Vardiya (Sabah)</th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Değişim</th>
                      <th className="px-3 py-2.5 text-left text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">2. Vardiya (Akşam)</th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-mono uppercase tracking-widest text-slate-600 font-bold">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(groupedRows[grp.line] || []).map((r) => {
                      const hasS1 = !!r.shift1_personnel;
                      const hasS2 = !!r.shift2_personnel;
                      const rowStatus = hasS1 && hasS2 ? "full" : (hasS1 || hasS2) ? "partial" : "empty";
                      return (
                        <tr
                          key={`${r.line}-${r.vehicle_no}`}
                          data-testid={`shift-row-${r.line}-${r.vehicle_no}`}
                          className="border-b border-slate-100 hover:bg-slate-50/60 align-middle"
                        >
                          <td className="px-3 py-2.5 text-center">
                            <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded bg-slate-900 text-white text-xs font-heading font-bold">{r.vehicle_no}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center min-w-0">
                              <VehicleSelect
                                value={r.plate}
                                onChange={(v) => updateCell(r._idx, "plate", v)}
                                vehicles={vehicles}
                                disabled={!canEdit}
                                testid={`plate-${r.line}-${r.vehicle_no}`}
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center min-w-0">
                              {canEdit ? (
                                <PersonSelect
                                  value={r.shift1_personnel}
                                  onChange={(v) => updateCell(r._idx, "shift1_personnel", v)}
                                  personnel={personnelList}
                                  disabled={!canEdit}
                                  testid={`s1-${r.line}-${r.vehicle_no}`}
                                />
                              ) : hasS1 ? (
                                <span className={`truncate font-medium ${personnelList.includes(r.shift1_personnel) ? "text-slate-800" : "italic text-orange-700"}`} data-testid={`s1-view-${r.line}-${r.vehicle_no}`}>{r.shift1_personnel}</span>
                              ) : (
                                <span className="text-slate-300 font-mono">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {canEdit ? (
                              <CellInput
                                value={r.change_time}
                                onChange={(v) => updateCell(r._idx, "change_time", v)}
                                placeholder="14:30"
                                mono
                                readOnly={!canEdit}
                                className="text-center"
                                testid={`change-${r.line}-${r.vehicle_no}`}
                              />
                            ) : r.change_time ? (
                              <span className="inline-flex items-center justify-center h-6 px-2 rounded-full bg-amber-100 text-amber-800 text-xs font-mono font-bold border border-amber-200">
                                {r.change_time}
                              </span>
                            ) : (
                              <span className="text-slate-300 font-mono">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center min-w-0">
                              {canEdit ? (
                                <PersonSelect
                                  value={r.shift2_personnel}
                                  onChange={(v) => updateCell(r._idx, "shift2_personnel", v)}
                                  personnel={personnelList}
                                  disabled={!canEdit}
                                  testid={`s2-${r.line}-${r.vehicle_no}`}
                                />
                              ) : hasS2 ? (
                                <span className={`truncate font-medium ${personnelList.includes(r.shift2_personnel) ? "text-slate-800" : "italic text-orange-700"}`} data-testid={`s2-view-${r.line}-${r.vehicle_no}`}>{r.shift2_personnel}</span>
                              ) : (
                                <span className="text-slate-300 font-mono">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span
                              className={`inline-block w-2.5 h-2.5 rounded-full ${
                                rowStatus === "full"
                                  ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]"
                                  : rowStatus === "partial"
                                  ? "bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.5)]"
                                  : "bg-slate-200"
                              }`}
                              title={
                                rowStatus === "full"
                                  ? "İki vardiya dolu"
                                  : rowStatus === "partial"
                                  ? "Tek vardiya (Yatar / Tek Şoför)"
                                  : "Boş"
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Day-level notes (edit mode only) */}
      {!isPastDate && (
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <StickyNote className="w-5 h-5 text-amber-600" />
            <h3 className="font-heading font-bold text-lg text-slate-900">Günlük Notlar / Açıklamalar</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Bugüne özel ceza, yatar durumu, arıza veya operasyonel notlar. Serbest metin.</p>
          <Textarea
            value={dayNotes}
            onChange={(e) => { setDayNotes(e.target.value); setDirty(true); }}
            rows={5}
            readOnly={!canEdit}
            placeholder="Örn: 15A-1 aracına ceza kesildi. B1-3 yatar, kaporta atölyeye alındı. 15A-4 sürücüsü rapor aldı, yerine..."
            data-testid="day-notes"
            className="text-sm leading-relaxed"
          />
          {dirty && canEdit && (
            <div className="mt-3 text-xs text-amber-700 font-medium">Kaydedilmemiş değişiklikler var — üstteki "Vardiyayı Kaydet" butonuna basın.</div>
          )}
        </CardContent>
      </Card>
      )}

      <HistorySearchModal open={historyOpen} onClose={() => setHistoryOpen(false)} onPickDate={(d) => { setDate(d); setHistoryOpen(false); }} />
    </div>
  );
}

function StatChip({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-slate-200 rounded px-3 py-1.5 flex items-center gap-2 bg-slate-50">
      <Icon className={`w-4 h-4 ${color}`} />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
        <div className={`font-heading font-bold text-sm ${color}`}>{value}</div>
      </div>
    </div>
  );
}

function CellInput({ value, onChange, placeholder, mono, className, readOnly, testid }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      data-testid={testid}
      className={`w-full h-9 px-2 py-1 rounded border border-slate-200 focus:border-blue-500 focus:outline-none text-sm ${mono ? "font-mono" : ""} ${readOnly ? "bg-transparent cursor-default" : "bg-white"} ${className || ""}`}
    />
  );
}

function PersonSelect({ value, onChange, personnel, disabled, testid }) {
  // Update 29.20 — "✎ Elle gir (geçici)" seçeneği: seçilince dropdown yerine
  // metin alanı açılır. Değer personel listesinde değilse geçici kabul edilir
  // ve kadrodaki şoföre dönene kadar italik+turuncu stilde gösterilir.
  const isTemp = !!value && !personnel.includes(value);
  const [typing, setTyping] = useState(isTemp);
  React.useEffect(() => { if (isTemp) setTyping(true); }, [isTemp]);
  if (typing) {
    return (
      <div className="flex items-center gap-1 w-full">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Geçici isim yaz"
          disabled={disabled}
          data-testid={testid ? `${testid}-temp-input` : undefined}
          className="flex-1 h-9 px-2 py-1 rounded border border-orange-300 bg-orange-50 italic text-orange-800 focus:border-orange-500 focus:outline-none text-sm"
        />
        <button
          type="button"
          onClick={() => { onChange(""); setTyping(false); }}
          disabled={disabled}
          title="Listeye dön"
          data-testid={testid ? `${testid}-temp-cancel` : undefined}
          className="h-9 px-2 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded bg-white"
        >↶</button>
      </div>
    );
  }
  return (
    <select
      value={value || ""}
      onChange={(e) => {
        if (e.target.value === "__MANUAL__") { setTyping(true); onChange(""); return; }
        onChange(e.target.value);
      }}
      disabled={disabled}
      data-testid={testid}
      className={`w-full h-9 px-2 py-1 rounded border border-slate-200 bg-white focus:border-blue-500 focus:outline-none text-sm ${disabled ? "cursor-default text-slate-500" : ""}`}
    >
      <option value="">— Personel seçin —</option>
      <option value="__MANUAL__" className="text-orange-700">✎ Elle gir (geçici)</option>
      {value && !personnel.includes(value) && (
        <option value={value}>{value} (kadro dışı)</option>
      )}
      {personnel.map((p) => (
        <option key={p} value={p}>{p}</option>
      ))}
    </select>
  );
}

function VehicleSelect({ value, onChange, vehicles, disabled, testid }) {
  // Vehicle dropdown — options are "CODE — PLATE" pulled from /vehicles.
  // Selecting a vehicle auto-fills the field with the full "CODE — PLATE" label
  // so the plate is bound to the chosen bus.
  const isLegacy = value && !vehicles.some((v) => v.label === value || v.plate === value);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      className={`w-full h-9 px-2 py-1 rounded border border-slate-200 bg-white focus:border-blue-500 focus:outline-none text-sm font-mono ${disabled ? "cursor-default text-slate-500" : ""}`}
    >
      <option value="">— Araç seçin —</option>
      {isLegacy && <option value={value}>{value} (kadro dışı)</option>}
      {vehicles.map((v) => (
        <option key={`${v.code}-${v.plate}`} value={v.label}>{v.display}</option>
      ))}
    </select>
  );
}

function PdfReportViewer({ url, loading, date }) {
  const displayUrl = url ? `${url}#toolbar=1&navpanes=0&view=FitH` : null;
  return (
    <Card className="overflow-hidden border-slate-300" data-testid="pdf-report-viewer">
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1E3A8A] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center">
            <FileDown className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-heading font-bold text-lg leading-none">Resmi Vardiya Raporu</div>
            <div className="text-blue-200 text-xs mt-1 font-mono">{date} · Salt okunur önizleme</div>
          </div>
        </div>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-100 hover:text-white text-xs font-mono underline"
            data-testid="open-pdf-newtab"
          >
            Yeni sekmede aç ↗
          </a>
        )}
      </div>
      <div className="bg-slate-100 relative" style={{ height: "calc(100vh - 320px)", minHeight: 640 }}>
        {loading || !url ? (
          <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">
            {loading ? "Rapor hazırlanıyor..." : "Bu tarih için rapor yok"}
          </div>
        ) : (
          <object
            data={displayUrl}
            type="application/pdf"
            className="w-full h-full"
            data-testid="pdf-object"
          >
            <iframe
              title="Vardiya Raporu Önizleme"
              src={displayUrl}
              className="w-full h-full border-0"
              data-testid="pdf-iframe"
            />
            <div className="p-6 text-center text-slate-600 text-sm">
              Tarayıcınız yerleşik PDF görüntüleyiciyi desteklemiyor.{" "}
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline font-medium">Raporu yeni sekmede aç</a>
              {" veya sağ üstteki "}<strong>PDF Olarak İndir</strong> butonunu kullanın.
            </div>
          </object>
        )}
      </div>
    </Card>
  );
}

function HistorySearchModal({ open, onClose, onPickDate }) {
  const [q, setQ] = useState("");
  const [plate, setPlate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [results, setResults] = useState([]);
  const vmap = useVehicleMap();
  const [busy, setBusy] = useState(false);

  const doSearch = async () => {
    setBusy(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (plate.trim()) params.plate = plate.trim();
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get("/shifts/search", { params });
      setResults(data || []);
      if ((data || []).length === 0) toast.info("Sonuç bulunamadı");
    } catch {
      toast.error("Arama başarısız");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (!open) { setResults([]); setQ(""); setPlate(""); setFrom(""); setTo(""); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl w-[92vw] max-h-[85vh] overflow-y-auto" data-testid="history-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <History className="w-5 h-5 text-blue-700" /> Vardiya Çizelgesi Geçmişi
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-600">Metinde Ara</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ör: ceza, yatar, arıza..." className="pl-8 h-10" data-testid="search-q" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Plaka</Label>
              <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="16 ADJ 618" className="mt-1 h-10 font-mono" data-testid="search-plate" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Tarih Aralığı</Label>
              <div className="flex gap-1 mt-1">
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10" data-testid="search-from" />
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10" data-testid="search-to" />
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={onClose}>Kapat</Button>
            <Button onClick={doSearch} disabled={busy} className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="do-search-btn">
              <Search className="w-4 h-4 mr-2" /> {busy ? "Aranıyor..." : "Ara"}
            </Button>
          </div>

          {results.length > 0 && (
            <div className="border border-slate-200 rounded max-h-[52vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Tarih</TableHead>
                    <TableHead>Hat / Araç</TableHead>
                    <TableHead>Plaka</TableHead>
                    <TableHead>1.Vardiya</TableHead>
                    <TableHead>2.Vardiya</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((r) => (
                    <TableRow key={r.id} data-testid={`search-row-${r.id}`}>
                      <TableCell className="font-mono text-sm">{r.date}</TableCell>
                      <TableCell className="text-sm">{r.line ? `${r.line} — ${r.vehicle_no}. Araç` : (r.hat_arac || "-")}</TableCell>
                      <TableCell className="font-mono text-sm">{r.plate ? vehicleLabel(vmap[normPlate(r.plate)]?.code, r.plate, brandOf(vmap, r.plate)) : ""}</TableCell>
                      <TableCell className="text-sm">{r.shift1_personnel}</TableCell>
                      <TableCell className="text-sm">{r.shift2_personnel}</TableCell>
                      <TableCell>
                        <button onClick={() => onPickDate(r.date)} className="text-xs text-blue-700 hover:underline" data-testid={`goto-${r.id}`}>Aç →</button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
